import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import axiosInstance from "../api/axiosInstance";
import {
  batchTotalExceedsMaxUploadSize,
  cumulativeMaxUploadExceededMessage,
  fileExceedsMaxUploadSize,
  maxUploadSizeExceededMessage,
  MAX_UPLOAD_FILE_SIZE_LABEL,
  sumFileSizesBytes,
} from "../utils/uploadFileSizeLimit";

// Maps a File object to its MIME type, used when requesting presigned URLs
const getContentType = (file) => {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop().toLowerCase();
  const map = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    txt: "text/plain",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    doc: "application/msword",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    m4a: "audio/mp4",
    mp4: "audio/mp4",
    aac: "audio/aac",
    flac: "audio/flac",
    ogg: "audio/ogg",
  };
  return map[ext] || "application/octet-stream";
};

// Generate UUID for batch ID
const generateUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: Generate UUID v4 manually
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const UploadDataModal = ({
  isOpen,
  onClose,
  selectedHousehold,
  onUploadSuccess,
  projectId,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileTypeError, setFileTypeError] = useState("");
  const [uploadingTranscript, setUploadingTranscript] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [sendingBot, setSendingBot] = useState(false);
  const [botError, setBotError] = useState("");
  const [botSuccess, setBotSuccess] = useState("");

  if (!isOpen) return null;

  const ALLOWED_EXTENSIONS = ["pdf", "png", "jpg", "jpeg", "txt", "docx"];

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      const invalidFiles = filesArray.filter((file) => {
        const ext = file.name.split(".").pop().toLowerCase();
        return !ALLOWED_EXTENSIONS.includes(ext);
      });

      if (invalidFiles.length > 0) {
        setFileTypeError(
          `Unsupported file format${invalidFiles.length > 1 ? "s" : ""}: ${invalidFiles.map((f) => f.name).join(", ")}. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}.`
        );
        return;
      }

      const mergedFiles = [...selectedFiles];
      filesArray.forEach((file) => {
        const alreadySelected = mergedFiles.some(
          (existing) =>
            existing.name === file.name && existing.size === file.size,
        );
        if (!alreadySelected) mergedFiles.push(file);
      });

      if (batchTotalExceedsMaxUploadSize(mergedFiles)) {
        setFileTypeError(
          cumulativeMaxUploadExceededMessage(sumFileSizesBytes(mergedFiles)),
        );
        return;
      }

      setFileTypeError("");
      setSelectedFiles(mergedFiles);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setError("");
    setSuccess("");
    setFileTypeError("");
    onClose();
  };

  const handleMultipleDocumentUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one file to upload.");
      return;
    }

    if (!selectedHousehold || !selectedHousehold.id) {
      setError("Please select a household first.");
      return;
    }

    if (!projectId) {
      setError("Project ID is missing. Please try again.");
      return;
    }

    const invalidFiles = selectedFiles.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      return !ALLOWED_EXTENSIONS.includes(ext);
    });

    if (invalidFiles.length > 0) {
      setFileTypeError(
        `Unsupported file format${invalidFiles.length > 1 ? "s" : ""}: ${invalidFiles.map((f) => f.name).join(", ")}. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}.`
      );
      return;
    }

    if (batchTotalExceedsMaxUploadSize(selectedFiles)) {
      setError(
        cumulativeMaxUploadExceededMessage(sumFileSizesBytes(selectedFiles)),
      );
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      const batchId = generateUUID();

      // Step 1: Get presigned URLs for all files
      const presignedResponse = await axiosInstance.post(
        `/api/files/households/${selectedHousehold.id}/presigned-urls`,
        {
          files: selectedFiles.map((file) => ({
            file_name: file.name,
            content_type: getContentType(file),
          })),
          batch_id: batchId,
          project_id: projectId,
        },
      );

      const { presigned_urls } = presignedResponse.data;
      if (!presigned_urls || presigned_urls.length === 0) {
        throw new Error("No presigned URLs received from server");
      }

      if (presigned_urls.length !== selectedFiles.length) {
        throw new Error("Mismatch between number of files and presigned URLs");
      }

      // Step 2: Upload all files to their respective presigned URLs
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const presignedUrlData = presigned_urls[index];

        // Use the content-type that S3 signed into the presigned URL — any
        // mismatch between this header and the signed value causes a 403.
        const contentType =
          new URL(presignedUrlData.presigned_url).searchParams.get(
            "content-type",
          ) || getContentType(file);

        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentComplete = (e.loaded / e.total) * 100;
              console.log(
                `Upload progress for ${file.name}: ${percentComplete.toFixed(2)}%`,
              );
            }
          });

          xhr.addEventListener("load", () => {
            if (xhr.status === 200 || xhr.status === 204) {
              resolve();
            } else {
              reject(
                new Error(
                  `Upload failed for ${file.name} with status ${xhr.status}`,
                ),
              );
            }
          });

          xhr.addEventListener("error", () => {
            reject(new Error(`Upload failed for ${file.name}`));
          });

          xhr.open("PUT", presignedUrlData.presigned_url);
          xhr.setRequestHeader("Content-Type", contentType);
          xhr.send(file);
        });
      });

      await Promise.all(uploadPromises);

      // Step 3: Confirm upload
      const confirmResponse = await axiosInstance.post(
        `/api/files/households/${selectedHousehold.id}/confirm-upload`,
        {
          batch_id: batchId,
          project_id: projectId,
        },
      );

      const { message } = confirmResponse.data;
      setSuccess(
        message ||
          `Successfully uploaded ${selectedFiles.length} document(s) and queued for processing.`,
      );
      setSelectedFiles([]);
      if (onUploadSuccess) onUploadSuccess();

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Document upload error:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to upload documents. Please try again.";
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleExcelUpload = async (file) => {
    if (fileExceedsMaxUploadSize(file)) {
      setError(maxUploadSizeExceededMessage(file));
      return;
    }
    try {
      setUploading(true);
      setError("");
      setSuccess("");

      const formData = new FormData();
      formData.append("file", file);
      formData.append("household_id", selectedHousehold.id);

      const response = await axiosInstance.post("/upload-excel", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess("Excel file uploaded successfully!");
      if (onUploadSuccess) onUploadSuccess();

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Excel upload error:", error);
      setError(
        error.response?.data?.detail ||
          "Failed to upload Excel file. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (documentType, file) => {
    if (fileExceedsMaxUploadSize(file)) {
      setError(maxUploadSizeExceededMessage(file));
      return;
    }
    try {
      setUploading(true);
      setError("");
      setSuccess("");

      const formData = new FormData();
      formData.append("file", file);
      formData.append("household_id", selectedHousehold.id);
      formData.append("document_type", documentType);

      const response = await axiosInstance.post("/upload-document", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess(`${documentType} uploaded successfully!`);
      if (onUploadSuccess) onUploadSuccess();

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Document upload error:", error);
      setError(
        error.response?.data?.detail ||
          "Failed to upload document. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleTranscriptAudioUpload = async (file) => {
    if (!selectedHousehold || !selectedHousehold.id) {
      setError("Please select a household first.");
      return;
    }

    if (!projectId) {
      setError("Project ID is missing. Please try again.");
      return;
    }

    // Validate file type
    const fileName = file.name.toLowerCase();
    const transcriptExtensions = [".docx", ".doc", ".txt", ".pdf"];
    const audioExtensions = [
      ".mp3",
      ".wav",
      ".m4a",
      ".mp4",
      ".aac",
      ".flac",
      ".ogg",
    ];

    const isTranscript = transcriptExtensions.some((ext) =>
      fileName.endsWith(ext),
    );
    const isAudio = audioExtensions.some((ext) => fileName.endsWith(ext));

    if (!isTranscript && !isAudio) {
      setError(
        "Invalid file type. Please upload a transcript (.docx, .doc, .txt, .pdf) or audio (.mp3, .wav, .m4a, .mp4, .aac, .flac, .ogg) file.",
      );
      return;
    }

    // Determine document type based on file extension
    const documentType = isTranscript ? "Meeting Transcript" : "Meeting Audio";

    setUploadingTranscript(true);
    setError("");
    setSuccess("");

    try {
      const batchId = generateUUID();

      // Step 1: Get presigned URL
      const presignedResponse = await axiosInstance.post(
        `/api/files/households/${selectedHousehold.id}/presigned-urls`,
        {
          files: [
            {
              file_name: file.name,
              document_type: documentType,
              content_type: getContentType(file),
            },
          ],
          batch_id: batchId,
          project_id: projectId,
        },
      );

      const { presigned_urls } = presignedResponse.data;
      if (!presigned_urls || presigned_urls.length === 0) {
        throw new Error("No presigned URL received from server");
      }

      const presignedUrlData = presigned_urls[0];

      // Use the content-type that S3 signed into the presigned URL — any
      // mismatch between this header and the signed value causes a 403.
      const contentType =
        new URL(presignedUrlData.presigned_url).searchParams.get(
          "content-type",
        ) || getContentType(file);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            console.log(`Upload progress: ${percentComplete.toFixed(2)}%`);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || xhr.status === 204) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed"));
        });

        xhr.open("PUT", presignedUrlData.presigned_url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(file);
      });

      // Step 3: Confirm upload
      const confirmResponse = await axiosInstance.post(
        `/api/files/households/${selectedHousehold.id}/confirm-upload`,
        {
          batch_id: batchId,
          project_id: projectId,
        },
      );

      const { message } = confirmResponse.data;
      setSuccess(
        message || "Document successfully uploaded and queued for processing.",
      );
      if (onUploadSuccess) onUploadSuccess();

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Transcript/Audio upload error:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to upload file. Please try again.";
      setError(errorMessage);
    } finally {
      setUploadingTranscript(false);
    }
  };

  const handleScheduleBot = async () => {
    if (!meetingLink.trim()) {
      setBotError("Please enter a meeting link.");
      return;
    }

    setSendingBot(true);
    setBotError("");
    setBotSuccess("");

    try {
      await axiosInstance.post("/api/schedule-meeting-bot-manual", {
        meeting_url: meetingLink.trim(),
        household_id: selectedHousehold?.id,
      });

      setBotSuccess(
        "FastTrackr AI will join your meeting shortly, please admit it",
      );
      setMeetingLink("");

      // Optionally Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Schedule bot error:", error);
      setBotError("Not able to send bot");
    } finally {
      setSendingBot(false);
    }
  };

  const suggestedDocuments = selectedHousehold?.suggested_documents || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">
            Enrich Household Data
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition duration-200"
            disabled={uploading || uploadingTranscript}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Option 1: From Client Docs — Blue */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <svg
                    className="w-5 h-5 text-blue-600 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      From Client Docs
                    </h3>
                    <p className="text-sm text-gray-600">
                      Upload ID, Tax, Bank and Brokerage Docs — combined file
                      size must not exceed {MAX_UPLOAD_FILE_SIZE_LABEL} MB per
                      upload
                    </p>
                    {fileTypeError && (
                      <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <svg
                          className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                          />
                        </svg>
                        <p className="text-xs text-red-700">{fileTypeError}</p>
                      </div>
                    )}
                    {selectedFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <p className="text-xs text-gray-500 truncate flex-1 min-w-0">
                              {file.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.txt,.docx"
                    onChange={(e) => {
                      handleFileSelect(e.target.files);
                      e.target.value = "";
                    }}
                    className="hidden"
                    id="client-docs-upload"
                    multiple
                    disabled={uploading}
                  />
                  <label
                    htmlFor="client-docs-upload"
                    className={`px-4 py-2 bg-blue-500 text-white text-sm rounded-lg transition duration-200 ${
                      uploading
                        ? "opacity-50 cursor-not-allowed pointer-events-none"
                        : "hover:bg-blue-600 cursor-pointer"
                    }`}
                  >
                    Upload Docs
                  </label>
                  {selectedFiles.length > 0 && (
                    <button
                      onClick={handleMultipleDocumentUpload}
                      disabled={uploading}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition duration-200 disabled:opacity-50 flex items-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4" />
                          Uploading...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Option 2: From Upcoming Client Meeting — Green */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-green-600 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    From Upcoming Client Meeting
                  </h3>
                  <p className="text-sm text-gray-600">
                    Add{" "}
                    <span className="font-semibold text-blue-600">
                      transition@fasttrackr.ai
                    </span>{" "}
                    to your meeting invite to automatically enrich data
                  </p>
                </div>
              </div>
            </div>

            {/* Option 2.5: Meeting Bot — Blue */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Meeting Bot
                    </h3>
                    <p className="text-sm text-gray-600">
                      Send FastTrackr to your meeting
                    </p>
                  </div>
                </div>
                <div className="flex flex-col w-full md:w-auto gap-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Paste meeting link here..."
                      value={meetingLink}
                      onChange={(e) => setMeetingLink(e.target.value)}
                      disabled={sendingBot}
                      className="flex-1 md:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 outline-none transition duration-200"
                    />
                    <button
                      onClick={handleScheduleBot}
                      disabled={sendingBot}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition duration-200 disabled:opacity-50 flex items-center gap-2"
                    >
                      {sendingBot ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4" />
                          Sending...
                        </>
                      ) : (
                        "Send Now"
                      )}
                    </button>
                  </div>
                  {botError && (
                    <p className="text-xs text-red-500 font-medium">
                      {botError}
                    </p>
                  )}
                  {botSuccess && (
                    <p className="text-xs text-green-600 font-medium">
                      {botSuccess}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Option 3: From Past Client Meeting — Green */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <svg
                    className="w-5 h-5 text-green-600 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      From Past Client Meeting
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <input
                    type="file"
                    accept=".docx,.doc,.txt,.pdf,.mp3,.wav,.m4a,.mp4,.aac,.flac,.ogg"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        if (e.target.files.length > 1) {
                          setError("Please select only one file at a time.");
                          e.target.value = "";
                          return;
                        }
                        handleTranscriptAudioUpload(e.target.files[0]);
                        e.target.value = "";
                      }
                    }}
                    className="hidden"
                    id="transcript-audio-upload"
                    disabled={uploading || uploadingTranscript}
                  />
                  <label
                    htmlFor="transcript-audio-upload"
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg cursor-pointer transition duration-200 disabled:opacity-50"
                  >
                    {uploadingTranscript
                      ? "Uploading..."
                      : "Upload Transcript or Audio File"}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDataModal;
